import { useMemo } from 'react';
import { REPORT_CALLABLE, LEAD_SOURCE_REPORT_ACTION } from './reportContract';
import { useTrackerCall } from './reportCache';

/** 'l12m' is the rolling twelve months to the Sales snapshot, Leads & Partners only. */
export type CreationPeriod = 'wtd' | 'mtd' | 'qtd' | 'fytd' | 'cytd' | 'l12m';
export interface LeadFilters { period?: CreationPeriod; windowStart?: string; windowEnd?: string; adviserId?: string }
export interface LeadSourceRow {
  leadSource: string; deals: number; loanAmountCents: number;
  settled: number; settledLoanAmountCents: number; inFlight: number; lost: number; unknown?: number;
  /**
   * V6 stage columns, attainment on this row's leads (leadSourceStages.ts on the server). Optional
   * so an older payload renders the live table unchanged, without blank columns.
   */
  meetingBooked?: number; meetingBookedLoanAmountCents?: number;
  lodged?: number; lodgedLoanAmountCents?: number;
  approved?: number; approvedLoanAmountCents?: number;
}
export interface PartnerRow extends Omit<LeadSourceRow, 'leadSource'> {
  key: string; partnerId: string | null; partnerLabel: string;
  issue: 'missing' | 'missing_id' | 'unresolved' | 'conflicting' | null;
}
export interface LeadSourceReportData {
  asAtDate: string; comparedAsAtDate: string; rows: LeadSourceRow[];
  totalDeals: number; totalLoanAmountCents: number; taxonomyWarning?: string;
  /** Leads carrying a loan amount (Sales, else Performance Deal Value), and how many came from Performance. */
  loanAmountCoverage?: { withAmount: number; fromPerformance: number };
  unattributedDeals: number; missingLeadSource: number;
  partnerRows?: PartnerRow[];
  cohort?: { from: string; to: string; settledPerCreatedBp: number | null } | null;
  coverage?: { missingCreatedDate: number; missingPartner: number; missingPartnerId: number;
    unresolvedPartner: number; conflictingPartner: number; partnerLabelsAvailable: boolean;
    /** Label-only leads attributed to the one partner ID that label carries. Absent on an older payload. */
    matchedPartnerByName?: number };
  advisers?: Array<{ id: string; name: string }>;
  insights?: LeadSourceInsights;
}

/** Mirrors reports/leadSourceInsights.ts. */
export interface LeadSourceTotals {
  deals: number; loanAmountCents: number;
  meetingBooked: number; meetingBookedLoanAmountCents: number;
  lodged: number; lodgedLoanAmountCents: number;
  approved: number; approvedLoanAmountCents: number;
  settled: number; settledLoanAmountCents: number;
  inFlight: number; lost: number; unknown: number;
}
export interface ConversionRow {
  key: string; label: string; leads: number; lodged: number; settled: number;
  lodgedPerLeadsBp: number | null; settledPerLodgedBp: number | null; settledPerLeadsBp: number | null;
}
export type ReferralTrend = 'Growing' | 'Steady' | 'Cooling';
export interface ReferrerRow {
  key: string; partnerId: string | null; partnerLabel: string;
  leads: number; settled: number; settledLoanAmountCents: number;
  lastReferralDate: string | null; last90: number; prior90: number; trend: ReferralTrend;
}
export interface SettlementsByMonth {
  months: string[]; lastMonthPartial: boolean;
  series: Array<{ leadSource: string; counts: number[]; valueCents: number[] }>;
  totals: { counts: number[]; valueCents: number[] };
  undatedSettled: number; undatedSettledLoanAmountCents: number;
}
export interface LeadSourceInsights {
  totals: LeadSourceTotals;
  avgSettledDealCents: number | null;
  topSource: { leadSource: string; settled: number; settledLoanAmountCents: number } | null;
  /** Absent on a whole-snapshot call: without a creation cohort there is no conversion. */
  conversion?: { source: ConversionRow[]; partner: ConversionRow[] };
  settlementsByMonth: SettlementsByMonth;
  referrers: { last90From: string; last90To: string; prior90From: string; prior90To: string; rows: ReferrerRow[] };
}
export type LeadSourceResult =
  | { status: 'ok'; data: LeadSourceReportData }
  | { status: 'no_sales_snapshot' }
  | { status: 'no_performance_snapshot'; salesAsAtDate: string }
  | { status: 'incompatible_snapshots'; salesAsAtDate: string; performanceAsAtDate: string };

export function useLeadSourceReport(companyId: string | undefined, asAtDate?: string, filters: LeadFilters = {}) {
  const { period, windowStart, windowEnd, adviserId } = filters;
  const request = useMemo(() => (companyId ? { action: LEAD_SOURCE_REPORT_ACTION, companyId,
    ...(asAtDate ? { asAtDate } : {}), ...(period ? { period } : {}),
    ...(windowStart ? { windowStart } : {}), ...(windowEnd ? { windowEnd } : {}),
    ...(adviserId ? { adviserId } : {}),
  } : null), [companyId, asAtDate, period, windowStart, windowEnd, adviserId]);
  // Cached for the session (reportCache.ts); an answer that arrives after the filters changed is dropped.
  const { data, loading, error, refetch } = useTrackerCall<LeadSourceResult>(REPORT_CALLABLE, request, {
    label: 'PipelineTracker leadSourceReport',
    describeError: (err) => {
      const code = (err as { code?: string }).code;
      return code === 'functions/permission-denied' ? 'Company-wide access is required for this report.'
        : code === 'functions/invalid-argument' ? 'Choose valid creation dates ending no later than the Sales snapshot.'
        : 'Leads & Partners could not be loaded.';
    },
  });
  return { result: request && !error ? data : null, loading, error, reload: refetch };
}
